import { FormEvent, useEffect, useState } from 'react'
import { FaEnvelope } from 'react-icons/fa'
import api from '../../../helpers/apiRequester'
import { useAuthUserData, useUpdateAuthUserData } from '../../../authUserProvider'
import { Banner, Button, Card, Field, Input, StatusPill, useToast } from 'ui'
import { apiErrorCode, apiErrorMessage, BreakableEmail, formatDateTime, UserProfile } from './shared'
import s from './styles.module.scss'

interface Props {
    profile: UserProfile
    onChange: (patch: Partial<UserProfile>) => void
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/

/**
 * Change the sign-in email. Two steps: request a code (emailed to the *new*
 * address), then confirm it. The current address stays live until confirmed.
 */
export const EmailCard = ({ profile, onChange }: Props) => {
    const authUser = useAuthUserData()
    const updateAuthUser = useUpdateAuthUserData()
    const { toast } = useToast()

    const pending = profile.pendingEmailChange
    const [newEmail, setNewEmail] = useState('')
    const [code, setCode] = useState('')
    const [busy, setBusy] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [notice, setNotice] = useState<string | null>(null)

    // Whatever's pending drives which step is shown; reset transient state
    // when it flips.
    useEffect(() => {
        setCode('')
        setError(null)
    }, [pending?.newEmail])

    const normalised = newEmail.trim().toLowerCase()
    const emailValid = EMAIL_REGEX.test(normalised)
    const sameAsCurrent = normalised === profile.email.toLowerCase()

    const request = async (event?: FormEvent, resendTo?: string) => {
        event?.preventDefault()
        const target = resendTo || normalised
        if (busy || !EMAIL_REGEX.test(target)) return
        setBusy(true)
        setError(null)
        setNotice(null)
        try {
            const response = await api({ url: '/profile-email-change-request', data: { newEmail: target } })
            if (!response.data?.success) throw new Error(response.data?.error || 'Request failed')
            onChange({ pendingEmailChange: { newEmail: response.data.newEmail, expiresAt: response.data.expiresAt } })
            setNewEmail('')
            setNotice(response.data.message || `We've sent a 6-digit code to ${response.data.newEmail}`)
        } catch (err: any) {
            setError(apiErrorMessage(err, 'Could not start the email change.'))
        } finally {
            setBusy(false)
        }
    }

    const confirm = async (event: FormEvent) => {
        event.preventDefault()
        const digits = code.replace(/\s+/g, '')
        if (busy || !/^\d{6}$/.test(digits)) return
        setBusy(true)
        setError(null)
        try {
            const response = await api({ url: '/profile-email-change-confirm', data: { code: digits } })
            if (!response.data?.success) throw new Error(response.data?.error || 'Confirmation failed')

            const email: string = response.data.email
            onChange({ email, emailVerified: true, pendingEmailChange: null })
            setNotice(null)
            if (authUser) {
                const nextUser = { ...authUser, email }
                updateAuthUser(nextUser)
                try {
                    window.localStorage.setItem('user', JSON.stringify(nextUser))
                } catch {
                    // best-effort
                }
            }
            toast({
                title: 'Email updated',
                description: response.data.warning
                    ? `${email} is now your sign-in email. The dashboard user record will catch up on the next sync.`
                    : `${email} is now your sign-in email. Use it next time you log in.`,
                tone: response.data.warning ? 'warn' : 'good',
            })
        } catch (err: any) {
            setError(apiErrorMessage(err, 'Could not confirm the code.'))
            if (apiErrorCode(err) === 'NO_PENDING') onChange({ pendingEmailChange: null })
            setCode('')
        } finally {
            setBusy(false)
        }
    }

    const cancel = async () => {
        if (busy) return
        setBusy(true)
        setError(null)
        try {
            await api({ url: '/profile-email-change-cancel', data: {} })
        } catch {
            // Best-effort: the code expires on its own anyway.
        } finally {
            onChange({ pendingEmailChange: null })
            setNotice(null)
            setBusy(false)
        }
    }

    return (
        <Card
            title="Sign-in email"
            subtitle="Used to log in and for account emails. We'll send a code to the new address before anything changes."
            actions={
                <StatusPill tone={profile.emailVerified ? 'good' : 'warn'} dot size="sm">
                    {profile.emailVerified ? 'Verified' : 'Unverified'}
                </StatusPill>
            }
        >
            <div className={s.form}>
                <div className={s.currentValue}>
                    <FaEnvelope aria-hidden="true" />
                    <span>
                        <BreakableEmail email={profile.email} />
                    </span>
                </div>

                {error && (
                    <Banner tone="bad" title="Couldn't update email">
                        {error}
                    </Banner>
                )}
                {notice && !error && <Banner tone="info">{notice}</Banner>}

                {pending ? (
                    <form className={s.form} onSubmit={confirm} noValidate>
                        <Banner tone="warn" title={`Confirm ${pending.newEmail}`}>
                            Enter the 6-digit code we emailed to that address. It expires at {formatDateTime(pending.expiresAt)}. Your
                            current email keeps working until you confirm.
                        </Banner>
                        <Field label="Verification code" required hint="Check the spam folder if it hasn't arrived after a minute.">
                            {(id) => (
                                <Input
                                    id={id}
                                    className={s.codeInput}
                                    value={code}
                                    onChange={(e) => setCode(e.target.value.replace(/[^\d\s]/g, '').slice(0, 7))}
                                    inputMode="numeric"
                                    autoComplete="one-time-code"
                                    placeholder="123 456"
                                    mono
                                    disabled={busy}
                                    autoFocus
                                />
                            )}
                        </Field>
                        <div className={s.formActions}>
                            <Button type="button" variant="ghost" onClick={cancel} disabled={busy}>
                                Cancel change
                            </Button>
                            <Button type="button" onClick={() => void request(undefined, pending.newEmail)} disabled={busy}>
                                Resend code
                            </Button>
                            <Button type="submit" variant="primary" loading={busy} disabled={!/^\d{6}$/.test(code.replace(/\s+/g, ''))}>
                                Confirm new email
                            </Button>
                        </div>
                    </form>
                ) : (
                    <form className={s.form} onSubmit={(e) => void request(e)} noValidate>
                        <Field
                            label="New email address"
                            error={newEmail && !emailValid ? 'Enter a valid email address' : sameAsCurrent ? 'That is already your email address' : undefined}
                        >
                            {(id) => (
                                <Input
                                    id={id}
                                    type="email"
                                    value={newEmail}
                                    onChange={(e) => setNewEmail(e.target.value)}
                                    autoComplete="email"
                                    placeholder="name@sentinellegal.co.uk"
                                    invalid={Boolean(newEmail) && (!emailValid || sameAsCurrent)}
                                    disabled={busy}
                                />
                            )}
                        </Field>
                        <div className={s.formActions}>
                            <Button type="submit" variant="primary" loading={busy} disabled={!emailValid || sameAsCurrent}>
                                Send verification code
                            </Button>
                        </div>
                    </form>
                )}
            </div>
        </Card>
    )
}
